import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sparkles, Send, AlertCircle, Eye,
  CheckCircle2, Square, CheckSquare, Loader2,
  GitCompareArrows, History, X,
} from 'lucide-react';
import '../styles/ClinicalReportTab.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'summary', label: 'Summary of Key Patterns' },
  { key: 'gaps', label: 'Identified Gaps and Contradictions' },
  { key: 'next_steps', label: 'Recommended Next Steps' },
];

const ClinicalReportTab = ({ caseId, token }) => {
  const [draft, setDraft] = useState(null);
  const [previousDraft, setPreviousDraft] = useState(null);
  const [released, setReleased] = useState(null);
  const [releasedHistory, setReleasedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState(null);
  const [statusBanner, setStatusBanner] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const loadReport = useCallback(async () => {
    if (!caseId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/cases/${caseId}/clinical-report`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setDraft(data.draft || null);
      setReleased(data.released || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, token]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleGenerate = async () => {
    if (!caseId) return;
    try {
      setGenerating(true);
      setStatusBanner(null);
      const res = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/clinical-report/generate`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ include_documents: true }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Generation failed');
      // Snapshot the current draft as previous (server also persists it).
      if (draft) setPreviousDraft({ ...draft, snapshotted_at: new Date().toISOString() });
      setDraft(data.draft);
      setStatusBanner({
        kind: 'success',
        text: `Draft generated — ${data.draft.raw_word_count} words across ${
          (data.draft.summary?.length || 0) +
          (data.draft.gaps?.length || 0) +
          (data.draft.next_steps?.length || 0)
        } paragraphs.`,
      });
    } catch (err) {
      setStatusBanner({ kind: 'error', text: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleToggle = async (section, paragraphId, included) => {
    if (!draft) return;
    // Optimistic update
    setDraft((prev) => ({
      ...prev,
      [section]: prev[section].map((p) =>
        p.id === paragraphId ? { ...p, included } : p
      ),
    }));
    try {
      await fetch(`${API_URL}/api/admin/cases/${caseId}/clinical-report`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({
          toggles: [{ section, paragraph_id: paragraphId, included }],
        }),
      });
    } catch (err) {
      setStatusBanner({ kind: 'error', text: `Save failed: ${err.message}` });
    }
  };

  const handlePdfPreview = () => {
    const url = `${API_URL}/api/admin/cases/${caseId}/clinical-report/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      })
      .catch((err) => setStatusBanner({ kind: 'error', text: err.message }));
  };

  const handleRelease = async () => {
    if (!draft) return;
    const includedTotal =
      draft.summary.filter((p) => p.included).length +
      draft.gaps.filter((p) => p.included).length +
      draft.next_steps.filter((p) => p.included).length;
    if (includedTotal === 0) {
      setStatusBanner({
        kind: 'error',
        text: 'At least one paragraph must be included before releasing.',
      });
      return;
    }
    if (!window.confirm(
      `Release ${includedTotal} paragraph${includedTotal === 1 ? '' : 's'} to the patient and email the PDF?`
    )) return;
    try {
      setReleasing(true);
      const res = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/clinical-report/release`,
        { method: 'POST', headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Release failed');
      // Roll prior released into history (cap 3) — server already does this
      // authoritatively, just keep UI in-sync without a refetch.
      if (released) {
        setReleasedHistory((prev) => [released, ...prev].slice(0, 3));
      }
      setReleased(data.released_report);
      const emailNote = data.email_status?.sent
        ? 'Patient email delivered.'
        : `Released, but email did not send: ${data.email_status?.error || 'no patient email on file'}.`;
      setStatusBanner({ kind: 'success', text: `Report released. ${emailNote}` });
    } catch (err) {
      setStatusBanner({ kind: 'error', text: err.message });
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <div className="clinical-report-loading">
        <Loader2 className="spin" size={28} />
        <p>Loading clinical report…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clinical-report-error">
        <AlertCircle size={28} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="clinical-report-container" data-testid="clinical-report-tab">
      <header className="cr-header">
        <div>
          <h3>Patient Clinical Report</h3>
          <p className="cr-subtitle">
            A 2-page, plain-language report drafted from this case&rsquo;s intake answers,
            smart-gaps findings, and uploaded records. Toggle any paragraph off before
            releasing.
            <span className="cr-credit">Drafted by Claude Sonnet 4.5</span>
          </p>
        </div>
        <div className="cr-actions">
          <button
            className="cr-btn cr-btn-primary"
            onClick={handleGenerate}
            disabled={generating || !caseId}
            data-testid="cr-generate-btn"
          >
            {generating ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
            {draft ? 'Regenerate Draft' : 'Generate Draft'}
          </button>
          {draft && (
            <button
              className="cr-btn cr-btn-secondary"
              onClick={handlePdfPreview}
              data-testid="cr-preview-pdf-btn"
            >
              <Eye size={16} /> Preview PDF
            </button>
          )}
          {draft && previousDraft && (
            <button
              className="cr-btn cr-btn-secondary"
              onClick={() => setShowDiff(true)}
              data-testid="cr-show-diff-btn"
              title="Compare to previous draft"
            >
              <GitCompareArrows size={16} /> Compare Draft
            </button>
          )}
          {(released || releasedHistory.length > 0) && (
            <button
              className="cr-btn cr-btn-secondary"
              onClick={() => setShowHistory(true)}
              data-testid="cr-show-history-btn"
              title="View prior released versions"
            >
              <History size={16} /> History ({(released ? 1 : 0) + releasedHistory.length})
            </button>
          )}
          {draft && (
            <button
              className="cr-btn cr-btn-release"
              onClick={handleRelease}
              disabled={releasing}
              data-testid="cr-release-btn"
            >
              {releasing ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
              Release to Patient
            </button>
          )}
        </div>
      </header>

      {statusBanner && (
        <div className={`cr-banner cr-banner-${statusBanner.kind}`}>
          {statusBanner.kind === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{statusBanner.text}</span>
        </div>
      )}

      {!draft && (
        <div className="cr-empty-state">
          <Sparkles size={32} />
          <h4>No draft yet</h4>
          <p>
            Click <strong>Generate Draft</strong> to have Claude write a 3-section report from this case&rsquo;s
            intake form, smart-gaps findings, and any extracted document text.
          </p>
        </div>
      )}

      {draft && (
        <>
          <div className="cr-meta">
            <span><strong>Generated:</strong> {formatDate(draft.generated_at)}</span>
            <span><strong>Words:</strong> {draft.raw_word_count} (target {draft.target_word_count})</span>
            <span><strong>Documents used:</strong> {draft.source_documents_count ?? 0}</span>
            {draft.last_edited_at && (
              <span><strong>Last edited:</strong> {formatDate(draft.last_edited_at)}</span>
            )}
          </div>

          {SECTIONS.map(({ key, label }) => (
            <ReportSection
              key={key}
              sectionKey={key}
              label={label}
              paragraphs={draft[key] || []}
              onToggle={handleToggle}
            />
          ))}

          {released && (
            <div className="cr-released-block" data-testid="cr-released-info">
              <div className="cr-released-title">
                <CheckCircle2 size={18} /> Last released to patient
                {released.version && (
                  <span className="cr-version-chip">v{released.version}</span>
                )}
              </div>
              <div className="cr-released-meta">
                <span>{formatDate(released.released_at)}</span>
                {released.released_by && <span>by {released.released_by}</span>}
              </div>
            </div>
          )}
        </>
      )}

      {showDiff && previousDraft && draft && (
        <DiffModal
          previous={previousDraft}
          current={draft}
          onClose={() => setShowDiff(false)}
        />
      )}

      {showHistory && (
        <HistoryModal
          current={released}
          history={releasedHistory}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

const ReportSection = ({ sectionKey, label, paragraphs, onToggle }) => {
  if (!paragraphs.length) return null;
  const includedCount = paragraphs.filter((p) => p.included).length;
  return (
    <section className="cr-section" data-testid={`cr-section-${sectionKey}`}>
      <div className="cr-section-head">
        <h4>{label}</h4>
        <span className="cr-section-counter">
          {includedCount} of {paragraphs.length} included
        </span>
      </div>
      <div className="cr-paragraph-list">
        {paragraphs.map((p) => (
          <article
            key={p.id}
            className={`cr-paragraph ${p.included ? '' : 'excluded'}`}
            data-testid={`cr-para-${sectionKey}-${p.id}`}
          >
            <button
              className="cr-toggle"
              onClick={() => onToggle(sectionKey, p.id, !p.included)}
              aria-label={p.included ? 'Exclude paragraph' : 'Include paragraph'}
              data-testid={`cr-toggle-${sectionKey}-${p.id}`}
            >
              {p.included ? <CheckSquare size={20} /> : <Square size={20} />}
            </button>
            <div className="cr-paragraph-body">
              {sectionKey === 'next_steps'
                ? <NextStepBlock item={p} />
                : sectionKey === 'gaps'
                  ? <GapBlock group={p} />
                  : <NarrativeBlock para={p} />}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

const NarrativeBlock = ({ para }) => (
  <>
    {para.heading && <div className="cr-paragraph-heading">{para.heading}</div>}
    <p>{para.body}</p>
  </>
);

const GapBlock = ({ group }) => (
  <>
    {group.heading && <div className="cr-paragraph-heading">{group.heading}</div>}
    {group.body && <p className="cr-gap-framing">{group.body}</p>}
    {(group.items || []).length > 0 && (
      <ol className="cr-gap-items">
        {group.items.map((item) => (
          <li key={item.id || item.lead}>
            <strong>{item.lead}</strong>
            {item.explanation && <span> — {item.explanation}</span>}
          </li>
        ))}
      </ol>
    )}
  </>
);

const NextStepBlock = ({ item }) => {
  const labelMap = {
    critical: 'Critical',
    important: 'Important',
    'if-time-allows': 'If time allows',
  };
  return (
    <>
      <div className="cr-next-row">
        <span className={`cr-priority cr-priority-${item.priority || 'important'}`}>
          {labelMap[item.priority] || 'Important'}
        </span>
        <span className="cr-next-ask">{item.ask}</span>
      </div>
      {item.why && <p className="cr-next-why">{item.why}</p>}
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// ---------------------------------------------------------------------------
// Diff modal — section-by-section, paragraph-keyed by id
// ---------------------------------------------------------------------------
const SECTION_LABEL = {
  summary: 'Summary',
  gaps: 'Gaps',
  next_steps: 'Next Steps',
};

const stringifyParagraph = (sectionKey, p) => {
  if (!p) return '';
  if (sectionKey === 'next_steps') {
    return [p.priority, p.ask, p.why].filter(Boolean).join(' · ');
  }
  if (sectionKey === 'gaps') {
    const items = (p.items || []).map((it) => `${it.lead || ''} — ${it.explanation || ''}`).join(' | ');
    return [p.heading, p.body, items].filter(Boolean).join(' · ');
  }
  return [p.heading, p.body].filter(Boolean).join(' · ');
};

const computeDiff = (prev, curr) => {
  const sections = ['summary', 'gaps', 'next_steps'];
  return sections.map((key) => {
    const prevList = prev[key] || [];
    const currList = curr[key] || [];
    const prevById = new Map(prevList.map((p) => [p.id, p]));
    const currById = new Map(currList.map((p) => [p.id, p]));
    const allIds = Array.from(new Set([...prevList.map((p) => p.id), ...currList.map((p) => p.id)]));
    const rows = allIds.map((id) => {
      const before = prevById.get(id);
      const after = currById.get(id);
      let status = 'unchanged';
      if (!before) status = 'added';
      else if (!after) status = 'removed';
      else if (stringifyParagraph(key, before) !== stringifyParagraph(key, after)) status = 'changed';
      return { id, before, after, status };
    });
    return { key, label: SECTION_LABEL[key], rows };
  });
};

const DiffModal = ({ previous, current, onClose }) => {
  const diff = useMemo(() => computeDiff(previous, current), [previous, current]);
  const counts = useMemo(() => {
    const c = { added: 0, changed: 0, removed: 0 };
    diff.forEach((s) => s.rows.forEach((r) => { if (r.status !== 'unchanged') c[r.status] += 1; }));
    return c;
  }, [diff]);

  return (
    <div
      className="patient-report-modal cr-diff-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="cr-diff-modal"
    >
      <div className="patient-report-modal-card cr-diff-card">
        <header className="patient-report-modal-head">
          <div>
            <h2>Compare Draft Versions</h2>
            <p>
              Previous: {formatDate(previous.snapshotted_at || previous.generated_at)}
              {' · '}
              Current: {formatDate(current.generated_at)}
            </p>
          </div>
          <button className="patient-report-close" onClick={onClose} aria-label="Close" data-testid="cr-diff-close-btn">
            <X size={22} />
          </button>
        </header>
        <div className="cr-diff-summary">
          <span className="cr-diff-chip cr-diff-added">{counts.added} added</span>
          <span className="cr-diff-chip cr-diff-changed">{counts.changed} changed</span>
          <span className="cr-diff-chip cr-diff-removed">{counts.removed} removed</span>
        </div>
        <div className="patient-report-body cr-diff-body">
          {diff.map((section) => (
            <section key={section.key} className="cr-diff-section">
              <h3>{section.label}</h3>
              {section.rows.length === 0 && <p className="cr-diff-empty">No paragraphs.</p>}
              {section.rows.map((row) => (
                <div key={row.id} className={`cr-diff-row cr-diff-row-${row.status}`}>
                  <div className="cr-diff-status-tag">{row.status.toUpperCase()}</div>
                  <div className="cr-diff-pair">
                    <div className="cr-diff-col cr-diff-before">
                      <h5>Previous</h5>
                      {row.before
                        ? <DiffParagraph sectionKey={section.key} p={row.before} />
                        : <p className="cr-diff-empty">—</p>}
                    </div>
                    <div className="cr-diff-col cr-diff-after">
                      <h5>Current</h5>
                      {row.after
                        ? <DiffParagraph sectionKey={section.key} p={row.after} />
                        : <p className="cr-diff-empty">—</p>}
                    </div>
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

const DiffParagraph = ({ sectionKey, p }) => {
  if (sectionKey === 'next_steps') return <NextStepBlock item={p} />;
  if (sectionKey === 'gaps') return <GapBlock group={p} />;
  return <NarrativeBlock para={p} />;
};

// ---------------------------------------------------------------------------
// History modal — lists last 3 released snapshots (current + history)
// ---------------------------------------------------------------------------
const HistoryModal = ({ current, history, onClose }) => {
  const versions = [];
  if (current) versions.push({ ...current, _label: 'Current' });
  history.forEach((h) => versions.push(h));

  return (
    <div
      className="patient-report-modal cr-diff-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="cr-history-modal"
    >
      <div className="patient-report-modal-card">
        <header className="patient-report-modal-head">
          <div>
            <h2>Released Version History</h2>
            <p>Last 3 versions sent to the patient (newest first)</p>
          </div>
          <button className="patient-report-close" onClick={onClose} aria-label="Close" data-testid="cr-history-close-btn">
            <X size={22} />
          </button>
        </header>
        <div className="patient-report-body">
          {versions.length === 0 && <p className="cr-diff-empty">No releases yet.</p>}
          {versions.map((v, idx) => (
            <article key={`${v.released_at || idx}`} className="cr-history-item">
              <header className="cr-history-head">
                <span className="cr-version-chip">v{v.version || (versions.length - idx)}</span>
                <span className="cr-history-date">{formatDate(v.released_at)}</span>
                {v.released_by && <span className="cr-history-by">by {v.released_by}</span>}
                {v._label && <span className="cr-history-current-tag">{v._label}</span>}
              </header>
              <div className="cr-history-counts">
                <span>{(v.summary || []).filter((s) => s.included).length} summary</span>
                <span>{(v.gaps || []).filter((s) => s.included).length} gaps</span>
                <span>{(v.next_steps || []).filter((s) => s.included).length} next-steps</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClinicalReportTab;
